if (!customElements.get('sticky-atc-bar-minimalist')) {
  customElements.define(
    'sticky-atc-bar-minimalist',
    class StickyAtcBarMinimalist extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.classes = {
          isOpen: 'sticky-atc-bar--show',
          isExpanded: 'sticky-atc-bar--expanded',
        };
        this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
      }

      get submitEl() {
        return (this._submitEl = this._submitEl || this.querySelector('[type="submit"]'));
      }

      get buyNowEl() {
        return this.querySelector('[data-sticky-buy-now]');
      }

      get mainPaymentButton() {
        return this.productFormActions?.querySelector(
          '.shopify-payment-button__button, .shopify-payment-button [role="button"]'
        );
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');

        if (this.submitEl) {
          this.submitEl.addEventListener('click', this.onClick.bind(this));
        }

        if (this.buyNowEl) {
          this.buyNowEl.addEventListener('click', this.onBuyNowClick.bind(this));
        }

        this._onResize = () => this.setBarHeight();
        this._onMediaChange = () => {
          this.initObservers();
          this.setBarHeight();
        };

        this.initObservers();
        this.syncWithMainProductForm();
        this.setBarHeight();
        window.addEventListener('resize', this._onResize, { passive: true });
        this.mobileQuery.addEventListener('change', this._onMediaChange);

        this.cartErrorUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, () => {
          this.resetButtonLoading();
          this.scrollToTop(this.productFormActions);
        });

        this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
          this.resetButtonLoading();
        });
      }

      disconnectedCallback() {
        document.body.classList.remove('sticky-atc-bar-enabled');
        this.teardownObservers();

        if (this._onResize) {
          window.removeEventListener('resize', this._onResize);
        }

        if (this._onMediaChange) {
          this.mobileQuery.removeEventListener('change', this._onMediaChange);
        }

        if (this.cartErrorUnsubscriber) {
          this.cartErrorUnsubscriber();
        }

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      isMobile() {
        return this.mobileQuery.matches;
      }

      teardownObservers() {
        this.showObserver?.disconnect();
        this.expandedObserver?.disconnect();
        this.showObserver = null;
        this.expandedObserver = null;
      }

      initObservers() {
        this.teardownObservers();

        if (!this.productFormActions) {
          this.classList.remove(this.classes.isOpen, this.classes.isExpanded);
          return;
        }

        if (this.isMobile()) {
          this.initMobileObservers();
        } else {
          this.initDesktopObservers();
        }
      }

      setExpanded(expanded) {
        this.classList.toggle(this.classes.isExpanded, expanded);
        const details = this.querySelector('.sticky-atc-bar__details');
        if (details) details.hidden = !expanded;
        this.setBarHeight();
      }

      initMobileObservers() {
        this.classList.add(this.classes.isOpen);
        this.setExpanded(false);

        const expandTarget =
          document.querySelector('.product__info-wrapper') || this.productFormActions;
        if (!expandTarget) return;

        this.expandedObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.setExpanded(scrolledPast);
            });
          },
          { threshold: 0 }
        );
        this.expandedObserver.observe(expandTarget);
      }

      initDesktopObservers() {
        this.setExpanded(false);
        const details = this.querySelector('.sticky-atc-bar__details');
        if (details) details.hidden = false;

        const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
        this.showObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const isAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.classList.toggle(this.classes.isOpen, isAboveViewport);
              this.setBarHeight();
            });
          },
          { threshold: 1, rootMargin }
        );
        this.showObserver.observe(this.productFormActions);
      }

      setBarHeight() {
        if (!this.classList.contains(this.classes.isOpen)) {
          document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
          return;
        }
        document.documentElement.style.setProperty('--sticky-atc-bar-height', `${this.offsetHeight}px`);
      }

      onBuyNowClick(event) {
        event.preventDefault();
        const paymentButton = this.mainPaymentButton;
        if (!paymentButton || paymentButton.disabled) {
          this.scrollToTop(this.productFormActions);
          return;
        }
        paymentButton.click();
      }

      onClick() {
        this.submitEl.setAttribute('aria-disabled', 'true');
        this.submitEl.classList.add('btn--loading');
      }

      resetButtonLoading() {
        this.submitEl.removeAttribute('aria-disabled');
        this.submitEl.classList.remove('btn--loading');
      }

      scrollToTop(target, offset = 200) {
        window.scrollTo({
          behavior: 'smooth',
          top: target.getBoundingClientRect().top - document.body.getBoundingClientRect().top - offset,
        });
      }

      updateButton(disable = true, text) {
        const addButton = this.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('.btn__text');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonText) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          if (addButtonText) addButtonText.textContent = FoxTheme.variantStrings.addToCartShort;
        }

        if (this.buyNowEl) {
          if (disable) {
            this.buyNowEl.setAttribute('disabled', 'disabled');
          } else {
            this.buyNowEl.removeAttribute('disabled');
          }
        }
      }

      updateVariant(variant) {
        const variantEl = this.querySelector('[data-sticky-variant]');
        if (!variantEl) return;
        variantEl.textContent = variant?.title || '';
      }

      updatePrice(variant) {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        if (!priceWrapper) return;

        const unitPrice = unitPriceWrapper?.querySelector('.f-price__unit');
        const { compare_at_price, price, unit_price_measurement } = variant;

        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        if (!variant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice?.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice?.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          unitPrice.innerHTML = `<span>${FoxTheme.Currency.formatMoney(
            variant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit?.() || ''}</span>`;
        } else {
          unitPriceWrapper?.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo?.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          this.updatePrice(variant);
          this.updateVariant(variant);
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }
    }
  );
}
